import {
  Building2,
  Car,
  Diamond,
  type LucideIcon,
  MapPin,
  Plane,
  Radio,
  Smartphone,
  User,
} from 'lucide-react'
import { lucideSvgMarkup } from '@/utils/lucide-svg'
import { normalizeMarkerType, pastelFor } from './marker-palette'

// Classic Google Maps-style teardrop pin markers — single solid pastel
// fill per entity type, white Lucide glyph in the bell. Built as SVG,
// rasterised at 2× and registered as map images so MapLibre's symbol
// layer can scale them per-zoom.

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  vehicle: Car,
  car: Car,
  person: User,
  phone: Smartphone,
  mobile: Smartphone,
  mobiledevice: Smartphone,
  btsstation: Radio,
  vtsstation: Radio,
  vts: Radio,
  bts: Radio,
  company: Building2,
  building: Building2,
  aircraft: Plane,
  address: MapPin,
  default: Diamond,
}

const SIZE_W = 48
const SIZE_H = 64
const GLYPH_PX = 22

function renderGlyphInner(Icon: LucideIcon): string {
  // Lucide always emits a 24-unit viewBox SVG; strip the wrapper so we can
  // place the glyph inside our own SVG at a known origin. Stroke and color
  // live on that wrapper, so the glyph takes them from the <g> it lands in.
  return lucideSvgMarkup(Icon)
    .replace(/^<svg[^>]*>/, '')
    .replace(/<\/svg>$/, '')
}

function buildPinSvg(type: string): string {
  const fill = pastelFor(type)
  const Icon = ICON_BY_TYPE[normalizeMarkerType(type)] ?? ICON_BY_TYPE.default
  const inner = renderGlyphInner(Icon)
  // Bell centred at (24, 22); pin tip at (24, 62). Glyph 22-unit, scale
  // 22/24 → 0.917×, translated so its centre lands on the bell centre.
  const cx = 24
  const cy = 22
  const tx = cx - GLYPH_PX / 2
  const ty = cy - GLYPH_PX / 2
  const scale = GLYPH_PX / 24

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SIZE_W} ${SIZE_H}" width="${SIZE_W}" height="${SIZE_H}">
  <defs>
    <filter id="pin-shadow-${type}" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="1.6" />
      <feOffset dx="0" dy="1.6" />
      <feComponentTransfer><feFuncA type="linear" slope="0.5" /></feComponentTransfer>
      <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
  </defs>
  <g filter="url(#pin-shadow-${type})">
    <path d="M24 2 C13 2 4 11 4 22 C4 32 12 42 24 62 C36 42 44 32 44 22 C44 11 35 2 24 2 Z"
          fill="${fill}" stroke="rgba(8,9,12,0.55)" stroke-width="1.6" stroke-linejoin="round" />
  </g>
  <g transform="translate(${tx} ${ty}) scale(${scale})" stroke="#ffffff" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    ${inner}
  </g>
</svg>`
}

async function svgToImageData(svg: string, ratio = 2): Promise<ImageData> {
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
  const img = new Image()
  img.decoding = 'async'
  img.src = url
  await img.decode()
  const w = SIZE_W * ratio
  const h = SIZE_H * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, w, h)
  return ctx.getImageData(0, 0, w, h)
}

export interface PinImageRecord {
  name: string
  data: ImageData
  pixelRatio: number
}

export async function buildAllPinImages(): Promise<PinImageRecord[]> {
  const types = Array.from(new Set(Object.keys(ICON_BY_TYPE)))
  const out: PinImageRecord[] = []
  for (const t of types) {
    out.push({
      name: pinImageName(t),
      data: await svgToImageData(buildPinSvg(t)),
      pixelRatio: 2,
    })
  }
  return out
}

export function pinImageName(type: string): string {
  return `pin-${normalizeMarkerType(type)}`
}
